// src/components/VehicleStatus/VehicleStatus.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Configure axios instance with retry logic
const axiosWithRetry = axios.create({
  timeout: 15000, // 15 second timeout
});

// Add retry interceptor
axiosWithRetry.interceptors.response.use(undefined, (err) => {
  const config = err.config;
  if (!config || !config.retry) return Promise.reject(err);
  
  config.__retryCount = config.__retryCount || 0;
  if (config.__retryCount >= config.retry) {
    return Promise.reject(err);
  }
  
  config.__retryCount += 1;
  return new Promise((resolve) => {
    setTimeout(() => resolve(axiosWithRetry(config)), config.retryDelay || 1000);
  });
});

const VehicleStatus = () => {
  const [deviceId, setDeviceId] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState('');

  // Simple geocoding function
  const reverseGeocode = async (latitude, longitude) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
        { timeout: 5000 }
      );
      
      const address = response.data.address;
      if (!address) return "Unknown location";
      
      return [
        address.road,
        address.suburb,
        address.city || address.town || address.village,
        address.country
      ].filter(Boolean).join(', ');
      
    } catch (error) {
      console.error("Geocoding failed:", error.message);
      return "Unknown location";
    }
  };

  // Helper function to check device existence
  const checkDeviceExists = async (deviceId) => {
    try {
      const token = "3585535a-137c-499e-82ad-af9feff7d8e7";
      const response = await axiosWithRetry.post(
        "https://icloud.assetscontrols.com:3443/OpenApi/Admin",
        {
          FAction: "QueryAdminAssetByAssetId",
          FTokenID: token,
          FAssetID: deviceId
        },
        {
          headers: { "Content-Type": "application/json" },
          retry: 2,
          timeout: 8000
        }
      );
      return response.data.FObject?.length > 0;
    } catch (error) {
      console.error("Device check failed:", error.message);
      return false;
    }
  };

  const getVehicleStatus = async () => {
    const newRequestId = uuidv4();
    setRequestId(newRequestId);
    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      // Validate input
      if (!deviceId.trim()) {
        throw {
          success: false,
          message: "Device ID is required",
          details: {
            received: { deviceId: !!deviceId }
          }
        };
      }

      const token = process.env.REACT_APP_TOKEN_ID;
      if (!token) {
        throw {
          success: false,
          message: "Client configuration error - missing API token",
          details: {
            required_env_variable: "REACT_APP_TOKEN_ID"
          }
        };
      }

      // Check if device exists
      const deviceExists = await checkDeviceExists(deviceId);
      if (!deviceExists) {
        throw {
          success: false,
          message: "Device not found in system",
          details: {
            deviceId,
            suggestions: [
              "Verify the device ID is correct",
              "Check if the device is properly registered"
            ]
          }
        };
      }

      // Try multiple endpoints
      const endpoints = [
        "https://icloud.assetscontrols.com:3443/OpenApi/LBS",
        "http://icloud.assetscontrols.com:8092/OpenApi/LBS"
      ];

      let locationData;
      let lastError;

      for (const endpoint of endpoints) {
        try {
          locationData = await axiosWithRetry.post(
            endpoint,
            {
              FAction: "QueryLBSMonitorListByFGUIDs",
              FTokenID: token,
              FGUIDs: deviceId,
              FType: 1
            },
            {
              headers: { 
                "Content-Type": "application/json",
                "X-Request-ID": newRequestId
              },
              retry: 2,
              timeout: 10000
            }
          );
          break; // Success, exit loop
        } catch (error) {
          lastError = error;
          console.error(`Attempt failed for ${endpoint}:`, error.message);
          continue;
        }
      }

      if (!locationData) {
        throw lastError || new Error("All API endpoints failed");
      }

      // Handle empty response
      if (!locationData.data.FObject || locationData.data.FObject.length === 0) {
        throw {
          success: false,
          message: "Device found but no location data available",
          details: {
            deviceId,
            suggestions: [
              "Check if device is powered on",
              "Verify device has cellular signal",
              "Confirm GPS antenna is properly connected"
            ]
          }
        };
      }

      const device = locationData.data.FObject[0];
      
      // Calculate offline duration
      const lastUpdate = new Date(device.FGPSTime);
      const now = new Date();
      const offlineMs = now - lastUpdate;
      
      const offlineDays = Math.floor(offlineMs / (1000 * 60 * 60 * 24));
      const offlineHours = Math.floor((offlineMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const offlineMinutes = Math.floor((offlineMs % (1000 * 60 * 60)) / (1000 * 60));

      // Get address
      const address = await reverseGeocode(device.FLatitude, device.FLongitude);

      // Format response
      setStatus({
        success: true,
        data: {
          Company: "Exhibit IT Solutions Ltd",
          Mileage: device.FMileage || 0,
          Speed: device.FSpeed || 0,
          "GPS time": device.FGPSTime,
          "Fuel level": device.FFuelValue1 !== -1 ? `${device.FFuelValue1}L(${device.FFuelValue2}%)` : "N/A",
          "Run status": `Offline [${offlineDays}d ${offlineHours}h ${offlineMinutes}min]`,
          "LAT,LONG": `${device.FLatitude},${device.FLongitude}`,
          Status: [
            `ACC: ${device.FACC === 1 ? "Open" : "Close"}`,
            `Engine: ${device.FMotor === 1 ? "On" : "Off"}`,
            `Fuel pump: ${device.FFuelCut === 1 ? "Cut" : "Normal"}`,
            `Door: ${device.FDoor === 1 ? "Open" : "Close"}`
          ].join("; "),
          Location: address
        }
      });

    } catch (error) {
      console.error("API Error:", {
        code: error.code,
        message: error.message,
        requestId: newRequestId
      });

      const errorMap = {
        ETIMEDOUT: {
          message: "Server request timeout",
          solution: "Try again later or contact API provider"
        },
        ECONNREFUSED: {
          message: "Service unavailable"
        },
        ENOTFOUND: {
          message: "API endpoint not found"
        }
      };

      const errorInfo = errorMap[error.code] || {
        message: error.message || "Internal server error"
      };

      setError({
        ...errorInfo,
        ...error,
        requestId: newRequestId
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vehicle-status-container p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Vehicle Status Check</h2>
      
      <div className="search-section mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="Enter Device ID"
            className="flex-1 p-2 border border-gray-300 rounded"
          />
          <button
            onClick={getVehicleStatus}
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            {loading ? 'Checking...' : 'Check Status'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-indicator p-4 bg-blue-50 rounded mb-4">
          <p>Fetching vehicle status...</p>
        </div>
      )}

      {error && (
        <div className="error-message p-4 bg-red-50 border-l-4 border-red-500 rounded mb-4">
          <h3 className="font-bold text-red-700">{error.message}</h3>
          
          {error.details && (
            <div className="error-details mt-3">
              {error.suggestions && (
                <div className="suggestions mb-3">
                  <h4 className="font-semibold text-sm">Suggestions:</h4>
                  <ul className="list-disc list-inside text-sm">
                    {error.suggestions.map((suggestion, i) => (
                      <li key={i}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="technical-details p-2 bg-gray-100 rounded text-xs">
                <pre>{JSON.stringify(error.details, null, 2)}</pre>
              </div>
            </div>
          )}
          
          <div className="mt-3 text-xs text-gray-500">
            Request ID: {error.requestId || requestId}
          </div>
        </div>
      )}

      {status && (
        <div className="status-container bg-white shadow rounded-lg overflow-hidden">
          <div className="status-header bg-green-50 p-4 border-b">
            <h3 className="font-bold text-green-800">Vehicle Status Report</h3>
            <div className="meta-info text-sm text-gray-600 mt-1">
              <span>Device ID: {deviceId}</span>
              <span className="mx-2">•</span>
              <span>Request ID: {requestId}</span>
            </div>
          </div>
          
          <div className="status-body p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(status.data).map(([key, value]) => (
                <div key={key} className="status-item">
                  <h4 className="font-semibold text-gray-700">{key}</h4>
                  <p className={key === 'Run status' && value.includes('Offline') ? 'text-red-600' : ''}>
                    {value}
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="status-footer bg-gray-50 p-3 text-xs text-gray-500 border-t">
            <p>Report generated: {new Date().toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleStatus;